'use client';

import React, { useEffect, useMemo } from 'react';
import type { StepConfig, FormValues, FormInstance, ShowIfCondition } from '../types';
import { getAddMoreConfig } from '../utils/addMoreConfig';
import { evaluateConditions } from '../utils/conditions';
import { mergeFormValues } from '../utils/valueMerger';

interface UseEnableConditionCleanupProps {
  allSteps: StepConfig[];
  values: FormValues;
  completedForms: Record<string, FormInstance[]>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  localStorageKey?: string;
}

/**
 * Returns true if all field IDs referenced in the condition exist in values.
 * Used to avoid deleting instances when we're on a different section and condition fields aren't in scope yet.
 */
function allConditionFieldsPresent(
  condition: ShowIfCondition | undefined,
  values: FormValues
): boolean {
  if (!condition) return true;
  if (Array.isArray(condition)) {
    return condition.every((c) => c.fieldId !== undefined && values[c.fieldId] !== undefined);
  }
  if ('conditions' in condition) {
    return (condition.conditions || []).every((c) => {
      if ('fieldId' in c) {
        return c.fieldId !== undefined && values[c.fieldId] !== undefined;
      }
      return allConditionFieldsPresent(c as ShowIfCondition, values);
    });
  }
  return true;
}

/**
 * Hook to delete instances when enableCondition evaluation changes to false
 * This ensures that when a condition becomes false (e.g., hasChildren changes from "Yes" to "No"),
 * all related instances are automatically removed
 */
export const useEnableConditionCleanup = ({
  allSteps,
  values,
  completedForms,
  setCompletedForms,
  localStorageKey,
}: UseEnableConditionCleanupProps) => {
  const mergedValues = useMemo(() => {
    return mergeFormValues(values, completedForms, localStorageKey);
  }, [values, completedForms, localStorageKey]);

  useEffect(() => {
    const stepsWithEnableCondition = allSteps.filter((step) => {
      const config = getAddMoreConfig(step);
      return config && config.enableCondition && config.groupKey;
    });

    // Check each groupKey that has instances
    setCompletedForms((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      stepsWithEnableCondition.forEach((step) => {
        const config = getAddMoreConfig(step);
        if (!config || !config.groupKey || !config.enableCondition) return;

        const groupKey = config.groupKey;
        const instances = prev[groupKey] || [];

        // Skip if no instances exist
        if (instances.length === 0) return;

        // Evaluate the enableCondition with current and previous values
        const isEnabled = evaluateConditions(config.enableCondition, mergedValues);

        // Only delete if condition is explicitly false AND all condition fields are present in mergedValues.
        // Skip deletion when we're on a different section (e.g. estate) and condition fields like hasChildren aren't in scope yet.
        const fieldsPresent = allConditionFieldsPresent(config.enableCondition, mergedValues);
        if (!isEnabled && fieldsPresent) {
          // Condition explicitly false - delete instances
          if (updated[groupKey] && updated[groupKey].length > 0) {
            updated[groupKey] = [];
            hasChanges = true;
          }
        }
      });

      // Update ref for next comparison
      // prevValuesRef.current = values;

      return hasChanges ? updated : prev;
    });
  }, [allSteps, mergedValues, setCompletedForms, values]); // Removed completedForms from dependencies
};
